import { computed, ref, toValue, type MaybeRef } from 'vue';
import { useStore } from '@/store';
import { useAcl } from './useAcl';
import type { UserWorkspace } from '@/services/models';

export type WorkspaceFormMode = 'edit' | 'create';

export type UseWorkspaceOptions = {
  mode?: MaybeRef<WorkspaceFormMode>;
};

export const useWorkspace = (options: UseWorkspaceOptions = {}) => {
  const store = useStore();
  const { aclCanCreateOnSection, selectOnlyPrivateWorkspaces } = useAcl();

  const mode = options.mode ?? ref<WorkspaceFormMode>('create');

  const workspacesList = computed((): UserWorkspace[] => {
    const userWorkspaces = store.user?.workspaces ?? [];
    if (toValue(mode) === 'edit') {
      return userWorkspaces;
    }
    const workspaces = selectOnlyPrivateWorkspaces.value
      ? store.allPrivateWorkspaces
      : userWorkspaces;
    return workspaces.filter((workspace: UserWorkspace) =>
      aclCanCreateOnSection({ workspaceId: workspace.id }),
    );
  });

  const defaultWorkspace = computed((): UserWorkspace | undefined => {
    const selectedWorkspaceIds = store.user?.selectedWorkspaces ?? [];
    const list = workspacesList.value;
    return (
      (selectedWorkspaceIds.length &&
        list.find((workspace: UserWorkspace) =>
          selectedWorkspaceIds.includes(workspace.id),
        )) ||
      list.find((workspace: UserWorkspace) => workspace.name === 'Master') ||
      list[0]
    );
  });

  const allPublicWorkspacesIds = computed(() => store.allPublicWorkspacesIds);

  return {
    mode,
    selectOnlyPrivateWorkspaces,
    workspacesList,
    defaultWorkspace,
    allPublicWorkspacesIds,
  };
};
